// Pure selectors that turn one EnrollmentRun into what Step 6, the read only view and the
// tray show: banners and table rows. One place for every banner rule (PCD-9663).
// See EnrollmentVerification.spec.md (ENV-FR-002, 003, 005, 006) and EnrollmentTray.spec.md (ENT-FR-002).
import {
  errorText,
  isHostSideCheck,
  plural,
} from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/helpers'
import type { VerifyBackendResult } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/model'
import { GAVE_UP_TEXT } from './enrollmentRuns'
import type { EnrollmentRun, HostEnrollment, StorageCheck } from './model'

export type BannerVariant = 'primary' | 'success' | 'warning' | 'error' | 'neutral'

export interface Banner {
  variant: BannerVariant
  icon: 'sync' | 'check' | 'warning' | 'error' | 'clock' | 'ban'
  title: string
  message: string
}

export type StepTwoRowStatus =
  | 'queued'
  | 'checking'
  | 'skipped'
  | 'passed'
  | 'warning'
  | 'failed'
  | 'notSupported'
  | 'couldntRun'

export interface StepTwoRow {
  key: string
  serviceHost: string
  hostName: string
  backend: string
  status: StepTwoRowStatus
  check: string
  detail?: string
  retryable: boolean
}

export type TrayStatus = 'inProgress' | 'success' | 'attention' | 'failed'

export interface TrayItem {
  id: string
  status: TrayStatus
  message: string
}

// A host that hasn't settled yet, either on its way in or still getting roles.
const isPendingHost = (host: HostEnrollment) =>
  host.phase === 'preparing' || host.phase === 'applying'

const hostNameFor = (run: EnrollmentRun, hostId: string) =>
  run.hosts.find((host) => host.hostId === hostId)?.hostName ?? hostId

// ENV-FR-002/ENT-FR-002: roles of the run, used in "Hosts have all N roles."
export const roleCount = (run: EnrollmentRun): number =>
  [
    run.selection.hypervisor,
    run.selection.imageLibrary,
    run.selection.remoteSupport,
    run.selection.dns,
    run.selection.backends.length > 0,
  ].filter(Boolean).length

// ENV-FR-002: the Step 1 banner, from how far the hosts of the run have gotten.
export const stepOneBanner = (run: EnrollmentRun): Banner => {
  if (run.hosts.some(isPendingHost)) {
    return {
      variant: 'primary',
      icon: 'sync',
      title: 'Applying roles...',
      message:
        "This can take several minutes. You can close this window. You'll be able to get back to this screen.",
    }
  }
  const completed = run.hosts.filter((host) => host.phase === 'completed')
  const failed = run.hosts.filter((host) => host.phase === 'failed')
  if (failed.length === 0) {
    return {
      variant: 'success',
      icon: 'check',
      title: 'Roles applied',
      message: `Hosts have all ${plural(roleCount(run), 'role')}.`,
    }
  }
  if (completed.length === 0) {
    return {
      variant: 'error',
      icon: 'error',
      title: "Roles weren't applied",
      // TODO(design): not in Figma, placeholder copy for the all failed case.
      message: 'No host got its roles. Storage checks are skipped.',
    }
  }
  return {
    variant: 'warning',
    icon: 'warning',
    title: `Roles applied on ${completed.length} of ${run.hosts.length} hosts`,
    message: 'Some hosts failed. Storage checks are skipped for these hosts.',
  }
}

// ENV-FR-003: what a row without a result says. A skipped row says why, and a check waiting to
// be sent again says what the last try answered, so a long retry doesn't look stuck.
const pendingCheckText = (check: StorageCheck): string => {
  if (check.state === 'skipped') {
    return check.skipReason === 'roleRemoved' ? 'Persistent Storage removed' : 'Roles failed'
  }
  if (check.state === 'queued' && check.notBefore && check.lastMessage) {
    return `Retrying. ${check.lastMessage}`
  }
  return '-'
}

// ENV-FR-003: Step 2 rows, one per host@backend before a result lands, one per returned
// check (or one summary row) once it has.
export const stepTwoRows = (run: EnrollmentRun): StepTwoRow[] => {
  const rows: StepTwoRow[] = []
  run.checks.forEach((check: StorageCheck) => {
    const base = {
      serviceHost: check.serviceHost,
      hostName: hostNameFor(run, check.hostId),
      backend: check.backend,
    }
    if (check.state === 'queued' || check.state === 'running' || check.state === 'skipped') {
      rows.push({
        ...base,
        key: check.serviceHost,
        status: check.state === 'running' ? 'checking' : check.state,
        check: pendingCheckText(check),
        retryable: false,
      })
      return
    }
    if (check.state === 'done') {
      // Contract: a 'done' check always carries its result.
      const result = check.result as VerifyBackendResult
      if (!result.supported || result.status === 'unsupported') {
        rows.push({
          ...base,
          key: check.serviceHost,
          status: 'notSupported',
          check: result.header,
          retryable: false,
        })
        return
      }
      result.checks.forEach((item) => {
        const status: StepTwoRowStatus =
          item.status === 'pass'
            ? 'passed'
            : item.status === 'warn'
              ? 'warning'
              : item.status === 'fail'
                ? 'failed'
                : 'skipped'
        rows.push({
          ...base,
          key: `${check.serviceHost}-${item.id}`,
          status,
          check: item.header,
          detail: item.remediation || item.message,
          retryable: status === 'warning' || status === 'failed',
        })
      })
      return
    }
    if (check.state === 'driverFailed') {
      rows.push({
        ...base,
        key: check.serviceHost,
        status: 'failed',
        check: "Storage driver didn't start",
        detail: check.result?.message,
        retryable: true,
      })
      return
    }
    if (check.state === 'gaveUp') {
      rows.push({
        ...base,
        key: check.serviceHost,
        status: 'failed',
        check: "Storage service didn't start",
        detail: `${GAVE_UP_TEXT} ${check.lastMessage || ''}`.trim(),
        retryable: true,
      })
      return
    }
    // check.state === 'error'
    rows.push({
      ...base,
      key: check.serviceHost,
      status: 'couldntRun',
      check: "Couldn't run the check",
      detail: errorText(check.error),
      retryable: check.error?.kind !== 'unavailable',
    })
  })
  return rows
}

// ENV-FR-005: a backend is unavailable when every host checked (a done result in hand) saw
// every one of its array side checks fail.
const unavailableBackends = (run: EnrollmentRun): string[] => {
  const backends = [...new Set(run.checks.map((check) => check.backend))]
  return backends.filter((backend) => {
    const done = run.checks.filter(
      (check) => check.backend === backend && check.state === 'done' && check.result,
    )
    if (done.length === 0) return false
    return done.every((check) => {
      const result = check.result as VerifyBackendResult
      const arraySide = result.checks.filter((item) => !isHostSideCheck(item.id))
      return arraySide.length > 0 && arraySide.every((item) => item.status === 'fail')
    })
  })
}

// ENV-FR-005: the Step 2 banner, first match wins.
export const stepTwoBanner = (run: EnrollmentRun): Banner | undefined => {
  if (run.selection.backends.length === 0) {
    return {
      variant: 'neutral',
      icon: 'ban',
      title: 'Storage checks skipped',
      message: 'No volume backends were selected. Hosts are compute-only.',
    }
  }
  if (run.hosts.some(isPendingHost)) {
    return {
      variant: 'neutral',
      icon: 'clock',
      title: 'Waiting for roles',
      message: 'Storage checks start once all roles are applied.',
    }
  }
  const inProgress = run.checks.filter(
    (check) => check.state === 'queued' || check.state === 'running',
  )
  if (inProgress.length > 0) {
    return {
      variant: 'primary',
      icon: 'sync',
      title: 'Checking storage connections...',
      message: `${plural(inProgress.length, 'check')} in progress.`,
    }
  }
  const unavailable = unavailableBackends(run)
  if (unavailable.length > 0) {
    return {
      variant: 'error',
      icon: 'error',
      title: 'Storage backend unavailable',
      message: `${unavailable.join(', ')} couldn't be reached, so host checks were skipped. An administrator needs to update the configuration.`,
    }
  }
  const rows = stepTwoRows(run)
  const attention = rows.filter(
    (row) => row.status === 'warning' || row.status === 'failed' || row.status === 'couldntRun',
  )
  if (attention.length > 0) {
    const total = rows.filter((row) => row.status !== 'skipped').length
    return {
      variant: 'warning',
      icon: 'warning',
      title: `${attention.length} of ${total} checks need attention`,
      message: 'Roles stay applied. Fix the issue on the host and retry the check.',
    }
  }
  if (run.checks.some((check) => check.state === 'skipped' && check.skipReason === 'roleFailed')) {
    return {
      variant: 'warning',
      icon: 'warning',
      title: 'Storage checks skipped',
      message: 'Checks were skipped for hosts where roles failed.',
    }
  }
  // ENR-FR-009: not in Figma. Names the hosts, since the user (or another admin) removed the role.
  const removed = [
    ...new Set(
      run.checks
        .filter((check) => check.state === 'skipped' && check.skipReason === 'roleRemoved')
        .map((check) => hostNameFor(run, check.hostId)),
    ),
  ]
  // Neutral, not a warning: the role was removed on purpose and there is nothing to fix here.
  if (removed.length > 0) {
    return {
      variant: 'neutral',
      icon: 'ban',
      title: 'Storage checks skipped',
      message:
        removed.length === 1
          ? `Persistent Storage was removed from ${removed[0]} before its check ran.`
          : `Persistent Storage was removed from ${removed.join(', ')} before their checks ran.`,
    }
  }
  return {
    variant: 'success',
    icon: 'check',
    title: 'All storage checks passed',
    message: 'Hosts can attach every selected backend.',
  }
}

// ENV-FR-006: replaces both banners once everything is done and clean.
export const combinedBanner = (run: EnrollmentRun): Banner | undefined => {
  if (run.selection.backends.length === 0) return undefined
  if (!run.hosts.every((host) => host.phase === 'completed')) return undefined
  const rows = stepTwoRows(run)
  if (rows.length === 0 || !rows.every((row) => row.status === 'passed')) return undefined
  return {
    variant: 'success',
    icon: 'check',
    title: 'Roles applied. All storage paths verified.',
    message: 'Hosts can attach every selected backend.',
  }
}

// ENT-FR-002: the one tray card for a run, same words as the banners it summarizes.
export const trayItem = (run: EnrollmentRun): TrayItem => {
  if (run.hosts.some(isPendingHost)) {
    return { id: run.id, status: 'inProgress', message: 'Step 1: Applying roles...' }
  }
  if (run.checks.some((check) => check.state === 'queued' || check.state === 'running')) {
    return { id: run.id, status: 'inProgress', message: 'Step 2: Checking storage connections...' }
  }
  if (run.hosts.every((host) => host.phase === 'failed')) {
    return { id: run.id, status: 'failed', message: stepOneBanner(run).title }
  }
  const combined = combinedBanner(run)
  if (combined) {
    return { id: run.id, status: 'success', message: combined.title }
  }
  const stepOne = stepOneBanner(run)
  if (stepOne.variant === 'warning') {
    return { id: run.id, status: 'attention', message: stepOne.title }
  }
  const stepTwo = stepTwoBanner(run)
  if (stepTwo && (stepTwo.variant === 'error' || stepTwo.variant === 'warning')) {
    return { id: run.id, status: 'attention', message: stepTwo.title }
  }
  if (run.selection.backends.length === 0) {
    return { id: run.id, status: 'success', message: 'Roles applied' }
  }
  // stepTwoBanner always answers once backends exist, see its own fallback.
  return { id: run.id, status: 'success', message: (stepTwo as Banner).title }
}
